import React, { useRef, useEffect, useCallback } from 'react';
import { Button } from 'antd';
import { useI18n } from '../../hooks/useI18n';
import { LANGUAGES, LANGUAGE_LABELS, CSS_CLASSES } from './Header.constants';
import { LanguageSelectorProps } from './Header.types';

const LanguageSelector: React.FC<LanguageSelectorProps> = React.memo(({ className, style }) => {
  const { language, changeLanguage, t } = useI18n();
  const announcementRef = useRef<HTMLDivElement>(null);

  const announceLanguageChange = useCallback(
    (newLanguage: string): void => {
      if (announcementRef.current) {
        const languageName =
          newLanguage === LANGUAGES.EN ? t('accessibility.english') : t('accessibility.spanish');

        announcementRef.current.textContent = `${t('accessibility.languageChanged')} ${languageName}`;

        setTimeout(() => {
          if (announcementRef.current) {
            announcementRef.current.textContent = '';
          }
        }, 2000);
      }
    },
    [t],
  );

  const handleLanguageChange = useCallback(
    (newLanguage: string): void => {
      if (newLanguage !== language) {
        changeLanguage(newLanguage);
        announceLanguageChange(newLanguage);
      }
    },
    [language, changeLanguage, announceLanguageChange],
  );

  const handleKeyDown = (event: React.KeyboardEvent, newLanguage: string): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleLanguageChange(newLanguage);
    }
  };

  useEffect(() => {
    const handleKeyboardShortcut = (event: KeyboardEvent): void => {
      if (event.altKey && event.key === 'l') {
        event.preventDefault();
        const newLanguage = language === LANGUAGES.EN ? LANGUAGES.ES : LANGUAGES.EN;
        handleLanguageChange(newLanguage);
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, [language, handleLanguageChange]);

  return (
    <>
      <div
        className={`language-selector-container ${className || ''}`}
        style={style}
        role="group"
        aria-label={t('accessibility.languageSelector')}
      >
        <div className="language-selector-wrapper">
          <div
            className={`language-selector-indicator ${language === LANGUAGES.ES ? CSS_CLASSES.SLIDE_RIGHT : CSS_CLASSES.SLIDE_LEFT}`}
            aria-hidden="true"
          />
          <Button
            type="text"
            onClick={() => handleLanguageChange(LANGUAGES.EN)}
            onKeyDown={(e) => handleKeyDown(e, LANGUAGES.EN)}
            className={`language-btn ${language === LANGUAGES.EN ? 'active' : ''}`}
            aria-pressed={language === LANGUAGES.EN}
            aria-label={`${t('accessibility.switchToEnglish')}${language === LANGUAGES.EN ? ` (${t('accessibility.currentLanguage')})` : ''}`}
            role="button"
            tabIndex={0}
          >
            {LANGUAGE_LABELS.EN}
          </Button>
          <span className="language-separator" aria-hidden="true">
            |
          </span>
          <Button
            type="text"
            onClick={() => handleLanguageChange(LANGUAGES.ES)}
            onKeyDown={(e) => handleKeyDown(e, LANGUAGES.ES)}
            className={`language-btn ${language === LANGUAGES.ES ? 'active' : ''}`}
            aria-pressed={language === LANGUAGES.ES}
            aria-label={`${t('accessibility.switchToSpanish')}${language === LANGUAGES.ES ? ` (${t('accessibility.currentLanguage')})` : ''}`}
            role="button"
            tabIndex={0}
          >
            {LANGUAGE_LABELS.ES}
          </Button>
        </div>
      </div>

      <div
        ref={announcementRef}
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />
    </>
  );
});

LanguageSelector.displayName = 'LanguageSelector';

export default LanguageSelector;
